import { useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';

import MiuThemeProvider from '@toolbox/style/theme/common/ThemeProvider';
import { Box, Button, Grid, GridItem, Input } from '@toolbox/components';
import { QuestionLayout } from '../../components/QuestionLayout/QuestionLayout';
import { stringOrUndefined } from '../../storyblok/utils/stringOrUndefined';
import { useLeadData, useWidgetContext } from '@widget/core';
import { CheckboxField } from '@widget/components';
import { AddressSuggestField } from '@toolbox/components/AddressSuggestField/index';
import { updateLeadData } from '@widget/api';
import { browserLogger } from '@widget/utils/datadog';
import {
  getLocationErrors,
  validateLocation,
} from '@toolbox/components/AddressSuggestField/utils/validateLocation';
import { APPOINTMENT_TYPE } from '@widget/api/types';
import { TEXT_DEFAULT } from './../constants';
import { getObjectFromKeys } from '@toolbox/utils';

import type { Location } from '@toolbox/components/AddressSuggestField/types';
import type {
  BookBrokerAppointmentOnSiteStepSchema,
  QuestionLayoutSchema,
} from '../../storyblok/schemas';
import type { Richtext } from 'storyblok-js-client';
import type { z } from 'zod';

type StepProps = z.infer<typeof BookBrokerAppointmentOnSiteStepSchema>;
type QuestionLayoutProps = z.infer<typeof QuestionLayoutSchema>;

export const BookBrokerAppointmentOnSiteStep: React.FC<StepProps> = ({
  phoneErrorText = TEXT_DEFAULT.PHONE_ERROR_MSG,
  ...props
}) => {
  const questionLayoutProps = props.children[0] as QuestionLayoutProps;
  const widget = useWidgetContext();
  const formData = widget.getFormData();
  const { leadData } = useLeadData();

  const zipCode = typeof formData.zipCode === 'string' ? formData.zipCode : '';
  const cityName = typeof formData.city === 'string' ? formData.city : '';
  const contForm = formData.contactForm as { phone: string };
  const phoneString = contForm?.phone || '';

  const [addressError, setAddressError] = useState<string>('');
  const [street, setStreet] = useState<string>('');
  const [numberHouse, setNumberHouse] = useState<string>('');
  const [city, setCity] = useState<string>(cityName);
  const [conventAccepted, setConventAccepted] = useState<boolean>(false);
  const [phone, setPhone] = useState(phoneString);
  const [errorPhone, setErrorPhone] = useState<null | string>(null);
  const [disabledPhone, setDisabledPhone] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const phoneRef = useRef<null | HTMLInputElement>(null);

  const isValid = !addressError && conventAccepted;
  const buttonDisabled = (isDirty && !isValid) || isSubmitting;

  const addressErrors = useMemo(() => {
    const errors = getObjectFromKeys(props.addressErrorsMap);
    return { ...TEXT_DEFAULT.ADDRESS_ERRORS, ...errors };
  }, [props.addressErrorsMap]);

  const handleSubmit = async () => {
    setIsDirty(true);
    if (!validateLocation({ city, street, postalCode: zipCode, number: numberHouse }, zipCode)) {
      setAddressError(
        getLocationErrors(
          { city, street, postalCode: zipCode, number: numberHouse },
          zipCode,
          addressErrors,
        ),
      );
      return;
    }
    if (!isValid) return;
    try {
      setIsSubmitting(true);
      if (leadData?.id) {
        const response = await updateLeadData(leadData.id, {
          city,
          streetAndNumber: `${street} ${numberHouse}`,
          contactForm: { phone },
        });
        if (response?.error && phoneErrorText) {
          setErrorPhone(phoneErrorText || null);
          setDisabledPhone(false);
          setIsSubmitting(false);
          phoneRef?.current?.focus();
          return;
        }
      }

      await widget.commit(
        props.id,
        {
          city,
          streetAndNumber: `${street} ${numberHouse}`,
          contactForm: { ...(formData.contactForm || {}), phone },
        },
        props.nextStep || undefined,
      );
    } catch (err) {
      browserLogger.error(err, 'book broker appointment lead update');
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkBoxText =
    formData.bookBrokerAppointmentType === APPOINTMENT_TYPE.ON_SITE
      ? props.checkBoxText || TEXT_DEFAULT.CHECKBOX_TEXT
      : props.checkBoxTextCall || TEXT_DEFAULT.CHECKBOX_TEXT_CALL;

  const handleSetAddress = (location: Location) => {
    setCity(location.city || '');
    setStreet(location.street || '');
    setNumberHouse(location.number || '');
    const isValid = validateLocation(location, zipCode);
    if (isValid) {
      setAddressError('');
    } else {
      setAddressError(getLocationErrors(location, zipCode, addressErrors));
    }
  };

  return (
    <QuestionLayout
      key={questionLayoutProps._uid}
      back={questionLayoutProps.back}
      submit={questionLayoutProps.submit}
      skip={questionLayoutProps.skip}
      title={questionLayoutProps.title}
      description={questionLayoutProps.description}
      backButtonText={stringOrUndefined(questionLayoutProps.backButtonText)}
      closeButtonText={stringOrUndefined(questionLayoutProps.closeButtonText)}
      submitButtonText={stringOrUndefined(questionLayoutProps.submitButtonText)}
      skipButtonText={stringOrUndefined(questionLayoutProps.skipButtonText)}
      socialProofMessage={props.socialProofMessage as Richtext}
      maxW={!questionLayoutProps.maxW ? undefined : questionLayoutProps.maxW}
      headerType={props.headerType}
    >
      <Grid>
        {!!leadData?.id && (
          <GridItem>
            <Input
              disabled={disabledPhone}
              name="bookPhone"
              value={phone}
              label="Telefonnummer"
              onChange={(ev) => {
                setPhone(ev.target.value);
                setErrorPhone(null);
              }}
              error={errorPhone}
              ref={phoneRef}
            />
          </GridItem>
        )}
        <GridItem>
          <AddressSuggestField
            label={props.addressLabel || TEXT_DEFAULT.ADDRESS_LABEL}
            initialValue={zipCode}
            error={addressError}
            setAddress={handleSetAddress}
          />
        </GridItem>

        <GridItem>
          <CheckboxField
            onInput={(ev) => {
              const target = ev.target as HTMLInputElement;
              setConventAccepted(target.value === 'true');
            }}
            name="bookConvent"
            label={checkBoxText}
          />
        </GridItem>
      </Grid>
      <Box css={{ textAlign: 'right' }} mt={3}>
        <Button
          disabled={buttonDisabled}
          onClick={handleSubmit}
          size={'large'}
          fullWidthMobile
          style={{ minWidth: '250px' }}
        >
          {isSubmitting ? (
            <MiuThemeProvider>
              <CircularProgress
                size={'sm'}
                sx={{
                  width: '22px',
                  verticalAlign: 'middle',
                }}
              />
            </MiuThemeProvider>
          ) : (
            props.ctaText || TEXT_DEFAULT.CTA_TEXT
          )}
        </Button>
      </Box>
    </QuestionLayout>
  );
};
